import React, { useState } from "react";
import { Box } from "@mui/material";
import { formatRelative } from "../ActivityTimeline/timelineUtils";
import CaseFullSummaryDialog from "./CaseFullSummaryDialog";
import { useCaseSummary } from "./useCaseSummary";
import { ACCENT, FAINT, INK_2, MONO, PANEL_BG, RULE } from "./styleTokens";

export type { CaseSummaryPayload } from "./useCaseSummary";

interface CaseSummaryCardProps {
  caseId: string;
  /** Refetch whenever the case gains or loses an activity — the server
   *  regenerates when its own activity count has moved on. */
  activityCount: number;
  /** Left half of the panel footer: last touch / touches / staleness chip. */
  footer?: React.ReactNode;
}

const linkSx = {
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  font: "inherit",
  fontWeight: 600,
  color: ACCENT,
  "&:hover": { textDecoration: "underline" },
};

const footerSx = {
  marginTop: "8px",
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap" as const,
  gap: "6px",
  fontSize: "11.5px",
  color: FAINT,
};

/**
 * The generated "what happened recently" note, drawn as the panel that sits on top of
 * the history feed: a filled grey box with a monospace eyebrow, 1–3 sentences
 * of body text and one tiny muted footer line.
 *
 *  - `source: "none"` / `error: "not_configured"` with no cached text → the
 *    panel keeps its eyebrow and footer so the card top never disappears.
 *  - `error: "unavailable"` with no text → one muted retry line.
 *
 * The whole-story summary lives behind the footer's "Full case summary" link
 * (CaseFullSummaryDialog), fetched only when that dialog opens.
 */
const CaseSummaryCard = ({
  caseId,
  activityCount,
  footer,
}: CaseSummaryCardProps) => {
  const { data, loading, regenerating, load, regenerate } = useCaseSummary(
    caseId,
    "recent",
    activityCount,
  );
  const [fullOpen, setFullOpen] = useState<boolean>(false);

  const summary = ((data && data.summary) || "").trim();
  const hasActivity = activityCount > 0;
  const generatedAt =
    data && data.generated_at ? new Date(data.generated_at) : null;
  const generatedLabel =
    generatedAt && !Number.isNaN(generatedAt.getTime())
      ? `Generated ${formatRelative(generatedAt)}`
      : "Generated";

  /** The body of the panel — one of: loading line, text, retry line, nothing. */
  let body: React.ReactNode = null;
  if (loading && !data) {
    body = (
      <Box
        aria-label="Loading recent activity summary"
        sx={{ fontSize: "13px", lineHeight: 1.5, color: FAINT }}
      >
        Generating summary…
      </Box>
    );
  } else if (summary) {
    body = (
      <Box
        sx={{
          fontSize: "13px",
          lineHeight: 1.55,
          color: INK_2,
          whiteSpace: "pre-wrap",
        }}
      >
        {summary}
      </Box>
    );
  } else if (data && data.error === "unavailable") {
    body = (
      <Box sx={{ fontSize: "13px", lineHeight: 1.5, color: FAINT }}>
        Summary unavailable right now ·{" "}
        <Box component="button" type="button" onClick={load} sx={linkSx} disabled={loading}>
          Try again
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: PANEL_BG,
        border: `1px solid ${RULE}`,
        borderRadius: "4px",
        padding: "9px 11px",
      }}
    >
      <Box
        component="span"
        sx={{
          display: "block",
          fontFamily: MONO,
          fontSize: "11px",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: FAINT,
          marginBottom: "5px",
        }}
      >
        Recent activity
      </Box>
      {body}
      {footer || summary || hasActivity ? (
        <Box sx={footerSx}>
          {footer}
          {footer && (summary || hasActivity) ? <Box sx={{ flexGrow: 1 }} /> : null}
          {summary || hasActivity ? (
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
              {summary ? (
                <>
                  <span>{generatedLabel}</span>
                  <span aria-hidden="true">·</span>
                  {regenerating ? (
                    <span>Regenerating…</span>
                  ) : (
                    <Box component="button" type="button" onClick={regenerate} sx={linkSx}>
                      Regenerate
                    </Box>
                  )}
                </>
              ) : null}
              {summary && hasActivity ? <span aria-hidden="true">·</span> : null}
              {hasActivity ? (
                <Box component="button" type="button" onClick={() => setFullOpen(true)} sx={linkSx}>
                  Full case summary
                </Box>
              ) : null}
            </Box>
          ) : null}
        </Box>
      ) : null}
      {hasActivity ? (
        <CaseFullSummaryDialog
          caseId={caseId}
          activityCount={activityCount}
          open={fullOpen}
          onClose={() => setFullOpen(false)}
        />
      ) : null}
    </Box>
  );
};

export default CaseSummaryCard;
